/**
 * EliyaQ Audio Engine
 * ─────────────────────────────────────────────────────────────────
 * Plays queue announcements in sequence:
 *   [chime] → [digits] → [connector] → [station_name]
 *
 * Each step tries uploaded AudioAsset clips first.
 * Falls back to Web Speech API TTS if any clip is missing.
 *
 * Usage:
 *   const engine = new AudioEngine(audioAssets, settings);
 *   engine.announce(ticketNumber, stationName, language, staffName, counterName);
 */

import BRAND from "../../brand";

export class AudioEngine {
  constructor(assets = [], settings = {}) {
    this.assets   = assets;   // array from GET /api/audio/
    this.settings = settings; // FacilitySettings fields
    this.queue    = [];       // announcement queue (one at a time)
    this.playing  = false;
    this.volume   = (settings.board_audio_volume ?? 80) / 100;
    this.rate     = settings.board_speech_rate ?? 1.0;
    this.gender   = settings.board_voice_gender || "default";
    this.language = settings.board_audio_language || "en";
  }

  // ── Public: queue an announcement ────────────────────────────────────────
  announce(ticketNumber, stationName, language, staffName = "", counterName = "", spokenName = "") {
    this.queue.push({ ticketNumber, stationName, language: language || this.language, staffName, counterName, spokenName });
    if (!this.playing) this._processNext();
  }

  // ── Internal: work through announcement queue ─────────────────────────────
  async _processNext() {
    if (this.queue.length === 0) { this.playing = false; return; }
    this.playing = true;
    const item = this.queue.shift();
    try {
      await this._playAnnouncement(item);
    } catch (e) {
      console.warn("[AudioEngine] Announcement error:", e);
    }
    // Small gap between announcements
    await this._sleep(600);
    this._processNext();
  }

  async _playAnnouncement({ ticketNumber, stationName, language, staffName, counterName, spokenName }) {
    const chimeEnabled = this.settings.board_chime_enabled !== false;
    const sayStaffName = this.settings.board_announce_staff_name && staffName;
    const sayCounter   = this.settings.board_announce_counter && counterName;

    // 1. Chime — tries an uploaded clip first, falls back to a synthesized
    // tone so the toggle actually does something out of the box. Most
    // facilities never upload a custom chime file; the old fallback here
    // was silent (no clip → no sound at all), which made "Chime before
    // announcements" look broken even though it was toggled on.
    if (chimeEnabled) {
      await this._playClip("chime", null, language)
        .catch(() => this._playDefaultChime());
    }

    // Brief pause after chime
    await this._sleep(400);

    // 1b. A client who has not responded to the earlier calls is addressed
    // by title + first name (the server only sends spokenName once the
    // facility's "after N calls" threshold has been passed).
    // White-label English builds speak it as one sentence:
    // "Hello Mr. Victor, with ticket number A 0 0 1, kindly move to Front Desk".
    const polite = !!spokenName && BRAND.englishOnly && language !== "tw";
    if (spokenName) {
      await this._tts(polite ? `Hello ${spokenName},` : `${spokenName},`, language);
      await this._sleep(polite ? 300 : 200);
    }

    // 2. "Ticket number" prefix via TTS (always TTS — short phrase)
    const prefix = polite ? "with ticket number" : "Ticket number";
    await this._tts(prefix, language);
    await this._sleep(150);

    // 3. Digits — e.g. "Q-047" → say "Q", then "0", "4", "7"
    await this._announceTicketNumber(ticketNumber, language);
    await this._sleep(polite ? 300 : 200);

    // 4. Connector phrase: "please proceed to"
    const connectorPlayed = polite ? false : await this._playClip("connector", null, language).catch(() => false);
    if (!connectorPlayed) {
      const connector = language === "tw" ? "kɔ" : polite ? "kindly move to" : "please proceed to";
      await this._tts(connector, language);
    }
    await this._sleep(150);

    // 5. Station name
    const stationPlayed = await this._playClip("station_name", stationName, language).catch(() => false);
    if (!stationPlayed) {
      await this._tts(stationName, language);
    }

    // 6. Optional counter number — off by default (matches the pre-existing
    // behavior, where the counter was never announced at all); a facility
    // opts in via Settings → Audio → "Announce counter number".
    if (sayCounter) {
      await this._sleep(150);
      const atPhrase = language === "tw" ? "wɔ" : "at";
      await this._tts(`${atPhrase} ${counterName}`, language);
    }

    // 7. Optional staff name
    if (sayStaffName) {
      await this._sleep(150);
      const withPhrase = language === "tw" ? "ɛka" : "with";
      await this._tts(`${withPhrase} ${staffName}`, language);
    }
  }

  // ── Announce ticket number digit by digit ─────────────────────────────────
  async _announceTicketNumber(ticketNumber, language) {
    // e.g. "Q-047" → chars: Q, 0, 4, 7
    const chars = ticketNumber.replace(/-/g, "").split("");
    for (const ch of chars) {
      const isDigit = /\d/.test(ch);
      if (isDigit) {
        const played = await this._playClip("number_digit", ch, language).catch(() => false);
        if (!played) await this._tts(ch, language);
      } else {
        // Letter prefix (Q, N, C etc.) — just TTS it
        await this._tts(ch, language);
      }
      await this._sleep(120);
    }
  }

  // ── Try to play an uploaded AudioAsset clip ────────────────────────────────
  _playClip(clipType, label, language) {
    return new Promise((resolve, reject) => {
      // Find matching asset
      let asset = this.assets.find(a =>
        a.clip_type === clipType &&
        a.language  === language &&
        (label === null || a.label?.toLowerCase() === label?.toLowerCase())
      );

      // Fallback: any language match
      if (!asset && label === null) {
        asset = this.assets.find(a => a.clip_type === clipType);
      }

      if (!asset?.audio_file_url) { reject(new Error("No clip")); return; }

      const audio     = new Audio(asset.audio_file_url);
      audio.volume    = this.volume;
      audio.onended   = () => resolve(true);
      audio.onerror   = () => reject(new Error("Play error"));
      audio.play().catch(reject);
    });
  }

  // ── Web Speech API TTS ────────────────────────────────────────────────────
  _tts(text, language, silent = false) {
    return new Promise((resolve) => {
      if (silent || !text?.trim()) { resolve(); return; }
      if (!window.speechSynthesis) { resolve(); return; }

      window.speechSynthesis.cancel(); // clear any ongoing speech

      const utter  = new SpeechSynthesisUtterance(text);
      utter.volume = this.volume;
      utter.rate   = this.rate;
      utter.pitch  = 1;
      utter.lang   = language === "tw" ? "ak-GH" : "en-GH";

      // Gender-aware voice selection
      const voices = window.speechSynthesis.getVoices();
      const FEMALE_PATTERNS = ["samantha","victoria","karen","moira","tessa","fiona","google uk english female","microsoft zira","allison","ava"];
      const MALE_PATTERNS   = ["daniel","alex","fred","tom","george","google uk english male","microsoft david","microsoft mark","oliver","aaron"];
      const langPrefix = language === "tw" ? "ak" : "en";
      const candidates = voices.filter(v => v.lang.toLowerCase().startsWith(langPrefix) || v.lang.toLowerCase().startsWith("en"));
      let chosenVoice = null;
      if (this.gender !== "default" && candidates.length > 0) {
        const patterns = this.gender === "female" ? FEMALE_PATTERNS : MALE_PATTERNS;
        chosenVoice = candidates.find(v => patterns.some(p => v.name.toLowerCase().includes(p)));
        if (!chosenVoice) chosenVoice = candidates[0]; // fallback
      } else {
        chosenVoice = candidates[0] || null;
      }
      if (chosenVoice) utter.voice = chosenVoice;

      utter.onend   = () => resolve();
      utter.onerror = () => resolve(); // never block on TTS error

      window.speechSynthesis.speak(utter);

      // Timeout safety — resolve after 4s no matter what
      setTimeout(resolve, 4000);
    });
  }

  _sleep(ms) {
    return new Promise(r => setTimeout(r, ms));
  }

  // ── Fallback chime when no AudioAsset clip is uploaded ─────────────────────
  // A short two-tone "ding-dong" synthesized via the Web Audio API, scaled
  // to the same volume setting as everything else. Never rejects — a
  // synth failure (e.g. no AudioContext support) just resolves silently
  // rather than blocking the rest of the announcement.
  _playDefaultChime() {
    return new Promise((resolve) => {
      try {
        const AudioCtx = window.AudioContext || window.webkitAudioContext;
        if (!AudioCtx) { resolve(); return; }
        const ctx = new AudioCtx();
        const now = ctx.currentTime;
        const playTone = (freq, start, duration) => {
          const osc  = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.type = "sine";
          osc.frequency.value = freq;
          gain.gain.setValueAtTime(0, now + start);
          gain.gain.linearRampToValueAtTime(this.volume * 0.3, now + start + 0.02);
          gain.gain.linearRampToValueAtTime(0, now + start + duration);
          osc.connect(gain);
          gain.connect(ctx.destination);
          osc.start(now + start);
          osc.stop(now + start + duration + 0.05);
        };
        playTone(880, 0, 0.25);
        playTone(660, 0.28, 0.3);
        setTimeout(() => { ctx.close().catch(() => {}); resolve(); }, 700);
      } catch {
        resolve();
      }
    });
  }

  // Update volume at runtime (from settings slider)
  setVolume(pct) {
    this.volume = pct / 100;
  }

  // Update speech rate at runtime (from settings slider)
  setRate(rate) {
    this.rate = rate;
  }

  // Update voice gender at runtime
  setGender(gender) {
    this.gender = gender;
  }

  // Update asset library (after upload)
  setAssets(assets) {
    this.assets = assets;
  }
}
